import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '../api'

export type ConsumableCategory = 'cleaning' | 'toiletries' | 'kitchen' | 'outdoor'

export interface Consumable {
  id: string
  name: string
  description: string | null
  category: ConsumableCategory
  location_id: string | null
  unit: string | null
  current_quantity: number
  min_quantity: number
  max_quantity: number
  photo_url: string | null
  photo_key: string | null
  purchase_link: string | null
  notes: string | null
  last_restocked_at: string | null
  last_restocked_by: string | null
  created_at: string
  updated_at: string
  location_name?: string
  restocked_by_name?: string
}

export interface ConsumableHistory {
  id: string
  consumable_id: string
  user_id: string
  action: 'restocked' | 'used' | 'adjusted'
  quantity_change: number
  quantity_after: number
  notes: string | null
  created_at: string
  user_name?: string
}

export interface CreateConsumableRequest {
  name: string
  description?: string
  category: ConsumableCategory
  location_id?: string
  unit?: string
  current_quantity?: number
  min_quantity?: number
  max_quantity?: number
  photo_key?: string
  purchase_link?: string
  notes?: string
}

export const useConsumablesStore = defineStore('consumables', () => {
  const consumables = ref<Consumable[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  const byCategory = computed(() => {
    const grouped: Record<ConsumableCategory, Consumable[]> = {
      cleaning: [],
      toiletries: [],
      kitchen: [],
      outdoor: [],
    }
    for (const item of consumables.value) {
      grouped[item.category].push(item)
    }
    return grouped
  })

  const lowStock = computed(() =>
    consumables.value.filter(c => c.current_quantity <= c.min_quantity)
  )

  const categoryLabels: Record<ConsumableCategory, string> = {
    cleaning: 'Cleaning Supplies',
    toiletries: 'Toiletries',
    kitchen: 'Kitchen',
    outdoor: 'Outdoor',
  }

  function getStockLevel(consumable: Consumable): 'empty' | 'low' | 'partial' | 'full' {
    const ratio = consumable.current_quantity / consumable.max_quantity
    if (ratio === 0) return 'empty'
    if (consumable.current_quantity <= consumable.min_quantity) return 'low'
    if (ratio >= 0.8) return 'full'
    return 'partial'
  }

  function getStockColor(level: 'empty' | 'low' | 'partial' | 'full'): string {
    switch (level) {
      case 'empty': return 'bg-red-500'
      case 'low': return 'bg-orange-500'
      case 'partial': return 'bg-yellow-500'
      case 'full': return 'bg-green-500'
    }
  }

  async function fetchConsumables(filters?: { category?: string; location?: string; low_stock?: boolean }) {
    loading.value = true
    error.value = null

    try {
      const params = new URLSearchParams()
      if (filters?.category) params.set('category', filters.category)
      if (filters?.location) params.set('location', filters.location)
      if (filters?.low_stock) params.set('low_stock', 'true')

      const query = params.toString() ? `?${params}` : ''
      const response = await api.get(`/api/consumables${query}`)
      consumables.value = response.consumables
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch consumables'
    } finally {
      loading.value = false
    }
  }

  async function fetchConsumable(id: string): Promise<{ consumable: Consumable; history: ConsumableHistory[] } | null> {
    try {
      const response = await api.get(`/api/consumables/${id}`)
      return { consumable: response.consumable, history: response.history }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch consumable'
      return null
    }
  }

  async function createConsumable(data: CreateConsumableRequest) {
    loading.value = true
    error.value = null

    try {
      const response = await api.post('/api/consumables', data)
      await fetchConsumables()
      return response.consumable
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create consumable'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function updateConsumable(id: string, data: Partial<CreateConsumableRequest>) {
    loading.value = true
    error.value = null

    try {
      await api.patch(`/api/consumables/${id}`, data)
      await fetchConsumables()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update consumable'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function recordUsage(id: string, quantity: number = 1, notes?: string) {
    try {
      const response = await api.post(`/api/consumables/${id}/use`, { quantity, notes })
      // Update local state
      const item = consumables.value.find(c => c.id === id)
      if (item) {
        item.current_quantity = response.quantity_after
      }
      return response.quantity_after
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to record usage'
      throw err
    }
  }

  async function recordRestock(id: string, quantity: number, notes?: string) {
    try {
      const response = await api.post(`/api/consumables/${id}/restock`, { quantity, notes })
      await fetchConsumables()
      return response.quantity_after
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to record restock'
      throw err
    }
  }

  async function deleteConsumable(id: string) {
    loading.value = true
    error.value = null

    try {
      await api.delete(`/api/consumables/${id}`)
      await fetchConsumables()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete consumable'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function getUploadUrl(filename: string, contentType: string) {
    const response = await api.post('/api/consumables/upload-url', { filename, contentType })
    return response
  }

  async function uploadPhoto(uploadUrl: string, file: File): Promise<string> {
    const API_BASE = import.meta.env.VITE_API_URL || ''
    const token = localStorage.getItem('session_token')

    const response = await fetch(`${API_BASE}${uploadUrl}`, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
        ...(token ? { 'X-Session-Token': token } : {}),
      },
      body: file,
    })

    if (!response.ok) {
      throw new Error('Failed to upload photo')
    }

    const data = await response.json()
    return data.key
  }

  return {
    consumables,
    byCategory,
    lowStock,
    categoryLabels,
    loading,
    error,
    getStockLevel,
    getStockColor,
    fetchConsumables,
    fetchConsumable,
    createConsumable,
    updateConsumable,
    recordUsage,
    recordRestock,
    deleteConsumable,
    getUploadUrl,
    uploadPhoto,
  }
})
